import type { FormEvent, ReactNode } from "react";
import { t } from "ttag";

import type {
  DatePickerUnit,
  RelativeDatePickerValue,
} from "metabase/querying/common/types";
import {
  ActionIcon,
  Divider,
  Flex,
  Group,
  Icon,
  Select,
  Text,
  Tooltip,
} from "metabase/ui";

import { NumberInputWithFallbackValue } from "../../NumberInputWithFallbackValue";
import type { DatePickerSubmitButtonProps } from "../../types";
import { renderDefaultSubmitButton } from "../../utils";
import { IncludeCurrentSwitch } from "../IncludeCurrentSwitch";
import { useTimeConfig } from "../use-time-config";
import {
  formatDateRange,
  getInterval,
  getUnitOptions,
  isOutOfBounds,
  setInterval,
} from "../utils";

import { setDefaultOffset, setUnit } from "./utils";

interface DateIntervalPickerProps {
  value: RelativeDatePickerValue;
  availableUnits: DatePickerUnit[];
  minDate?: Date;
  maxDate?: Date;
  renderSubmitButton?: (props: DatePickerSubmitButtonProps) => ReactNode;
  onChange: (value: RelativeDatePickerValue) => void;
  onSubmit: () => void;
}

export function DateIntervalPicker({
  value,
  availableUnits,
  minDate,
  maxDate,
  renderSubmitButton = renderDefaultSubmitButton,
  onChange,
  onSubmit,
}: DateIntervalPickerProps) {
  const timeConfig = useTimeConfig();
  const interval = getInterval(value);
  const unitOptions = getUnitOptions(value, availableUnits);
  const dateRangeText = formatDateRange(timeConfig, value);
  const outOfBounds = isOutOfBounds(timeConfig, value, minDate, maxDate);

  const handleIntervalChange = (inputValue: number | string) => {
    if (typeof inputValue === "number") {
      onChange(setInterval(value, inputValue));
    }
  };

  const handleUnitChange = (inputValue: string | null) => {
    const option = unitOptions.find((option) => option.value === inputValue);
    if (option) {
      onChange(setUnit(value, option.value));
    }
  };

  const handleStartingFromClick = () => {
    onChange(setDefaultOffset(value));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (outOfBounds) {
      return;
    }
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Flex p="lg" align="center">
        <NumberInputWithFallbackValue
          allowDecimal={false}
          value={interval}
          aria-label={t`Interval`}
          w="4rem"
          onChange={handleIntervalChange}
        />
        <Select
          data={unitOptions}
          value={value.unit}
          aria-label={t`Unit`}
          ml="lg"
          onChange={handleUnitChange}
          comboboxProps={{
            withinPortal: false,
            floatingStrategy: "fixed",
          }}
        />
        <Tooltip label={t`Starting from…`} position="bottom">
          <ActionIcon
            variant="subtle"
            size="lg"
            ml="sm"
            aria-label={t`Starting from…`}
            onClick={handleStartingFromClick}
          >
            <Icon name="arrow_left_to_line" />
          </ActionIcon>
        </Tooltip>
      </Flex>
      <Flex p="lg" pt={0}>
        <IncludeCurrentSwitch value={value} onChange={onChange} />
      </Flex>
      <Divider />
      <Group px="lg" py="sm" justify="space-between">
        <Group c="text-secondary" gap="sm">
          <Icon name="calendar" />
          <Text c="inherit">{dateRangeText}</Text>
        </Group>
        {renderSubmitButton({ value, isDisabled: outOfBounds })}
      </Group>
    </form>
  );
}
